import { HttpHelper } from "@/helper/HttpHelper";

// Client-side article service
// This file is safe to import in Client Components

const glossaryListCache = new Map();
const GLOSSARY_LIST_CACHE_TTL = 5 * 60 * 1000;

// Get Article List (Client-side)
export const getArticleList = async (request) => {
    const { page, search, category } = request;
    try {
        const query = new URLSearchParams();
        if (page) query.append('page', String(page));
        if (search) query.append('search', search);
        if (category && category !== 'All') query.append('category', category);

        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/article/list' + (query.toString() ? `?${query.toString()}` : '');
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Article list not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching article list:', error);
        return [];
    }
}

// Get Article list by category (Client-side)
export const getArticleListByCategory = async (slug) => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/category/' + slug + '/article';
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn(`Articles for category "${slug}" not found (404), returning empty array`);
            return [];
        }
        console.error('Error fetching articles by category:', error);
        return [];
    }
}

// Get homepage categories (Client-side / Server-safe)
export const getHomepageCategories = async () => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/category/homepage-categories';
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        // Handle both a bare array and a { data: [...] } wrapper
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        return [];
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Homepage categories not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching homepage categories:', error);
        return [];
    }
}

// Get Article Detail by id (Client-side)
export const getArticleDetailBySlug = async (slug) => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/article/show/' + slug;
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn(`Article with slug "${slug}" not found (404), returning null`);
            return null;
        }
        console.error('Error fetching article detail:', error);
        return null;
    }
}

// Get Glossary List (Client-side)
export const getGlossaryList = async (request = {}) => {
    const { page, search, perPage } = request;
    try {
        const query = new URLSearchParams();
        if (page) query.append('page', String(page));
        if (search) query.append('search', search);
        // Glossary is rendered as a single A–Z index, so fetch a large page.
        query.append('per_page', String(perPage || 500));

        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/glossary/list?' + query.toString();
        const cached = glossaryListCache.get(apiUrl);
        const now = Date.now();
        if (cached && now - cached.timestamp < GLOSSARY_LIST_CACHE_TTL) {
            return cached.data;
        }

        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        glossaryListCache.set(apiUrl, { data: response, timestamp: now });
        return response;
    } catch (error) {
        if (error?.response?.status === 404) {
            console.warn('Glossary list not found (404), returning empty array');
            return { data: [] };
        }
        console.error('Error fetching glossary list:', error);
        return { data: [] };
    }
}

// Get Glossary Detail by slug (Client-side)
export const getGlossaryDetailBySlug = async (slug) => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/glossary/show/' + slug;
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        if (error?.response?.status === 404) {
            console.warn(`Glossary with slug "${slug}" not found (404), returning null`);
            return null;
        }
        console.error('Error fetching glossary detail:', error);
        return null;
    }
}

// Get Post List
export const getNewsList = async (request) => {
    const { page } = request;
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/news/list?page=' + page;
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Post list not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching post list:', error);
        return [];
    }
}

// Get News Detail by id (Client-side)
export const getNewsDetailBySlug = async (slug) => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/news/show/' + slug;
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn(`News with slug "${slug}" not found (404), returning null`);
            return null;
        }
        console.error('Error fetching news detail:', error);
        return null;
    }
}

// Get Featured News
export const getFeaturedNews = async () => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/news/featured';
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Featured news not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching featured news:', error);
        return [];
    }
}

// Get Featured Article
export const getFeaturedArticle = async () => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/article/featured';
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Featured article not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching featured article:', error);
        return [];
    }
}

// Get Feed List (articles / news / market-insight)
export const getFeedList = async ({ type, page, per_page, search } = {}) => {
    try {
        const query = new URLSearchParams();
        if (type) query.append('type', type);
        if (page) query.append('page', String(page));
        if (per_page) query.append('per_page', String(per_page));
        if (search) query.append('search', search);
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/feed/list' + (query.toString() ? `?${query.toString()}` : '');
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        if (error?.response?.status === 404) {
            console.warn('Feed list not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching feed list:', error);
        return [];
    }
}

// Get Market Insights
export const getMarketInsights = async () => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/market-insights/list';
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Featured news not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching featured news:', error);
        return [];
    }
}

// Get Featured Market Insights
export const getFeaturedMarketInsights = async () => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/market-insights/featured';
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Featured market insights not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching featured market insights:', error);
        return [];
    }
}

// Get Market Insight Detail by slug
export const getMarketInsightDetailBySlug = async (slug) => {
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/market-insights/show/' + slug;
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        console.error('Error fetching market insight detail:', error);
        return null;
    }
}

// Get Contact Us
export const getContactUs = async (request) => {
    try {
        const { name, email, phone, message } = request;
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/contact/submit-query';
        const body = {
            name: name,
            email: email,
            phone: phone,
            message: message
        }
        const response = await HttpHelper.post(apiUrl, body).then((response) => response.data);
        return response;
    } catch (error) {
        console.error('Error fetching contact us:', error);
        throw error;
    }
}

// Get Similar Articles
export const getSimilarArticles = async (type, slug) => {
    let dataType = '';
    if(type === 'marketing') {
        dataType = 'market-insights';
    } else {
        dataType = type;
    }
    try {
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/' + dataType + '/similar/' + slug;
        const response = await HttpHelper.get(apiUrl).then((response) => response.data);
        return response;
    } catch (error) {
        // Handle 404 and other errors gracefully - don't break the build
        if (error?.response?.status === 404) {
            console.warn('Similar articles not found (404), returning empty array');
            return [];
        }
        console.error('Error fetching similar articles:', error);
        return [];
    }
}

// Contact CEO/Founder
export const contactCeo = async (request) => {
    const { recipient, first_name, last_name, company, title, phone, email, message, website } = request;
    const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/contact/ceo';
    const body = { recipient, first_name, last_name, company, title, phone, email, message, website: website || '' };
    const response = await HttpHelper.post(apiUrl, body).then((response) => response.data);
    return response;
}

// Newsletter Subscribe
export const newsletterSubscribe = async (request) => {
        const { email } = request;
        const apiUrl = process.env.NEXT_PUBLIC_BASE_URL + '/newsletter/subscribe';
        const body = { email: email };
        const response = await HttpHelper.post(apiUrl, body).then((response) => response.data);
        return response;
    
}

